import { FC, useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useVirtualizer } from '@tanstack/react-virtual';
import Banner from '@/components/UI/Banner';
import Button from '@/components/UI/Button';
import { downloadFile } from '@/components/UI/Table/Reusable/APITable/APITable.utils';
import { errorToast, successToast } from '@/helpers';
import useMountedState from '@/support/Hooks/fetch/useMountedState';
import { useModalAction } from '@/utils/modal';
import { useDataTable } from '@/components/UI/DataTableV2/DataTableProvider';
import { useScrollSync } from '@/components/UI/DataTableV2/hooks/useScrollSync';
import { useDataTableErrors } from '@/components/UI/DataTableV2/hooks/useDataTableErrors';
import { useColumns } from '@/components/UI/DataTableV2/hooks/useColumns';
import { useRows } from '@/components/UI/DataTableV2/hooks/useRows';
import { ScrollableGroupHeaders } from '@/components/UI/DataTableV2/headers/ScrollableGroupHeaders';
import { ScrollableColumnHeaders } from '@/components/UI/DataTableV2/headers/ScrollableColumnHeaders';
import { StickyPinnedColumnHeaders } from '@/components/UI/DataTableV2/headers/StickyPinnedColumnHeaders';
import {
  DataTableRowColumnHeaders,
  DataTableRowHeaderStickyContainer,
} from '@/components/UI/DataTableV2/headers/DataTableRowHeader';
import { StickyRowHeaders } from '@/components/UI/DataTableV2/headers/StickyRowHeaders';
import { StickyPinnedCells } from '@/components/UI/DataTableV2/cells/StickyPinnedCells';
import { VirtualizedCells } from '@/components/UI/DataTableV2/cells/VirtualizedCells';
import { DataTableToolbar } from '@/components/UI/DataTableV2/toolbar/DataTableToolbar';
import type {
  DataTableHighVolumeUpsertEvent,
  DataTableRowHeaderStateChangeEvent,
} from '@/components/UI/DataTableV2/DataTable.model';
import { DataTableEvent } from '@/components/UI/DataTableV2/DataTable.model';
import styles from './DataTableV2.module.scss';
import { _notNil } from '@/littledash';
import { useLegacyApiHook } from '@/support/Hooks/api/useLegacyApiHook';

const GROUP_HEADER_ROW_HEIGHT = 40;

export const DataTableView: FC = () => {
  const { dataTableService } = useDataTable();
  const { id: studyId, dataTableId } = useParams<{ id: string; dataTableId: string }>();
  const isMounted = useMountedState();
  const { openModal, closeModal } = useModalAction();

  const scrollElementRef = useRef<HTMLDivElement>(null);
  const columnHeadersRef = useRef<HTMLDivElement>(null);
  const rowHeadersRef = useRef<HTMLDivElement>(null);

  const errors = useDataTableErrors();
  const columns = useColumns();
  const rows = useRows();
  const [rowHeaderWidth, setRowHeaderWidth] = useState(dataTableService.rowHeaderWidth());

  const scrollableColumnCount = useMemo(() => {
    const leafColumns = dataTableService.getColumnGroupingData()?.leafColumns ?? columns;
    const pinnedColumns = dataTableService.getPinnedColumns();
    const pinnedDataColumnIds = new Set(pinnedColumns.map((p) => p.id));
    return leafColumns.filter((col) => !pinnedDataColumnIds.has(col.id)).length;
  }, [columns, dataTableService]);

  const { invoke: exportDatatable, loading: exportingDataTable } = useLegacyApiHook({
    method: 'GET',
    apiRoute: 'datatables.export',
    path: { studyId, dataTableId },
    options: {
      requestConfigOverrides: { responseType: 'blob' },
      invokeOnInit: false,
      onError: { slug: 'datatable-export', toast: false, throw: false, capture: false },
    },
  });

  useEffect(() => {
    const abortController = new AbortController();
    const rowHeaderStateChangeListener = (event: DataTableRowHeaderStateChangeEvent) => {
      setRowHeaderWidth(event.detail.rowHeaderWidth);
    };

    const pasteLimitExceededListener = () => {
      openModal('INFO', {
        text: 'A maximum of 20000 cells can be pasted.',
        btnTxt: 'OK',
      });
    };

    const highVolumeUpsertListener = (event: DataTableHighVolumeUpsertEvent): void => {
      const {
        detail: { totalChunkedRequests, pending, rejected },
      } = event;
      if (!dataTableService.readonly) {
        if (rejected.length > 0) {
          if (dataTableService.undoEnabled()) {
            dataTableService.undo();
            closeModal();
            errorToast(
              'There was a problem uploading your data. We have reverted the last change made. Please try again.'
            );
          } else {
            errorToast(
              'A problem occurred while uploading data, and the changes could not be automatically reverted. Please review your data.'
            );
          }
        } else {
          if (totalChunkedRequests === pending.length && _notNil(dataTableService)) {
            openModal('DT_HIGH_VOLUME_UPSERT', { dataTableService });
          }
        }
      }
    };

    dataTableService.subscribe(
      DataTableEvent.RowHeaderStateChange,
      rowHeaderStateChangeListener,
      abortController.signal
    );
    dataTableService.subscribe(DataTableEvent.PasteLimitExceeded, pasteLimitExceededListener, abortController.signal);
    dataTableService.subscribe(DataTableEvent.HighVolumeUpsert, highVolumeUpsertListener, abortController.signal);
    return () => {
      abortController.abort('teardown');
    };
  }, [dataTableService, openModal, closeModal]);

  const handleExportDataTable = async () => {
    const response = await exportDatatable({ path: { studyId, dataTableId } });
    if (isMounted()) {
      if (response.type === 'success') {
        downloadFile({ data: response.body, headers: response.headers });
        successToast(`Downloaded ${response.headers['file-name']}`);
      } else {
        errorToast('Could not export datatable. Please try again later.');
      }
    }
  };

  const dimensions = dataTableService.dimensions;
  const columnGroupingData = dataTableService.getColumnGroupingData();
  const pinnedColumns = dataTableService.getPinnedColumns();

  const leafColumns = columnGroupingData?.leafColumns ?? columns;
  const hasGroupedColumns = columnGroupingData !== null && columnGroupingData.columnGroups.length > 0;

  // Pinned columns are now only data columns (row headers are separate)
  const pinnedDataColumnIds = new Set(pinnedColumns.map((p) => p.id));

  const pinnedDataColumns = leafColumns.filter((col) => pinnedDataColumnIds.has(col.id));
  const scrollableColumns = leafColumns.filter((col) => !pinnedDataColumnIds.has(col.id));

  const pinnedColumnWidth = pinnedDataColumns.length * dimensions.cellWidth;

  const rowVirtualizer = useVirtualizer({
    count: rows.length,
    getScrollElement: () => scrollElementRef.current,
    estimateSize: () => dimensions.cellHeight,
    overscan: 10,
  });

  const columnVirtualizer = useVirtualizer({
    horizontal: true,
    count: scrollableColumnCount,
    getScrollElement: () => scrollElementRef.current,
    estimateSize: () => dimensions.cellWidth,
    overscan: 5,
  });

  useScrollSync({
    scrollElementRef,
    horizontalTargetRef: columnHeadersRef,
    verticalTargetRef: rowHeadersRef,
    rowVirtualizer,
    columnVirtualizer,
    dataTableService,
  });

  const virtualRows = rowVirtualizer.getVirtualItems();
  const virtualColumns = columnVirtualizer.getVirtualItems();

  const stickyWidth = rowHeaderWidth + pinnedColumnWidth;
  const groupHeaderHeight = hasGroupedColumns ? GROUP_HEADER_ROW_HEIGHT : 0;
  const totalHeaderHeight = groupHeaderHeight + dimensions.columnHeight;

  return (
    <div className="h-100 flex flex-column">
      <div className="flex-shrink-0 flex justify-between items-center ph3 pv2 bb b--moon-gray bg-white">
        <h3 className="f6 lh-title near-black fw5">{dataTableService.name}</h3>
        <Button onClick={handleExportDataTable} disabled={exportingDataTable}>
          Export
        </Button>
      </div>

      {dataTableService.readonly && (
        <Banner className="flex-shrink-0 mw6 ma3" info>
          <h3 className="f5 lh-title">You don&apos;t have access to edit this Datatable.</h3>
          <p className="f6 lh-title measure mt2">Request &apos;Write&apos; access from the Study author or owner.</p>
        </Banner>
      )}

      <div className="flex-shrink-0 bg-white">
        <DataTableToolbar />
      </div>

      <div className={styles.gridContainer}>
        <div className={styles.headerRow}>
          <div
            className={styles.stickyHeader}
            style={{
              width: `${stickyWidth}px`,
              height: `${totalHeaderHeight}px`,
            }}
          >
            <div className={styles.virtualContainer}>
              <DataTableRowColumnHeaders groupHeaderHeight={groupHeaderHeight} />
              <StickyPinnedColumnHeaders
                pinnedDataColumns={pinnedDataColumns}
                rowHeaderWidth={rowHeaderWidth}
                groupHeaderHeight={groupHeaderHeight}
                dimensions={dimensions}
                columnGroupingData={columnGroupingData}
              />
            </div>
            <div className={styles.zoneBorderRight} />
            <div className={styles.zoneBorderBottom} />
          </div>

          <div
            className={styles.scrollableHeader}
            style={{
              height: `${totalHeaderHeight}px`,
            }}
          >
            <div
              ref={columnHeadersRef}
              className={styles.virtualContainer}
              style={{
                width: `${columnVirtualizer.getTotalSize()}px`,
                height: `${totalHeaderHeight}px`,
              }}
            >
              {hasGroupedColumns && _notNil(columnGroupingData) && (
                <ScrollableGroupHeaders
                  columnGroupingData={columnGroupingData}
                  virtualColumns={virtualColumns}
                  stickyColumnCount={0}
                  dimensions={dimensions}
                  scrollElementRef={scrollElementRef}
                  pinnedColumnWidth={pinnedColumnWidth}
                />
              )}
              <ScrollableColumnHeaders
                virtualColumns={virtualColumns}
                scrollableColumns={scrollableColumns}
                stickyColumnCount={0}
                groupHeaderHeight={groupHeaderHeight}
                dimensions={dimensions}
                columnGroupingData={columnGroupingData}
              />
            </div>
            <div className={styles.zoneBorderBottom} />
          </div>
        </div>

        <div className={styles.bodyRow}>
          <div
            className={styles.stickyRows}
            style={{
              width: `${stickyWidth}px`,
            }}
          >
            <div
              ref={rowHeadersRef}
              className={styles.virtualContainer}
              style={{
                width: `${stickyWidth}px`,
                height: `${rowVirtualizer.getTotalSize()}px`,
              }}
            >
              <DataTableRowHeaderStickyContainer rowHeaderWidth={rowHeaderWidth}>
                <StickyRowHeaders
                  virtualRows={virtualRows}
                  rowHeaderWidth={rowHeaderWidth}
                  dimensions={dimensions}
                  errors={errors}
                />
              </DataTableRowHeaderStickyContainer>
              <StickyPinnedCells
                virtualRows={virtualRows}
                pinnedDataColumns={pinnedDataColumns}
                rowHeaderWidth={rowHeaderWidth}
                dimensions={dimensions}
              />
            </div>
            <div className={styles.zoneBorderRight} />
          </div>

          <div ref={scrollElementRef} className={styles.mainGrid}>
            <div
              className={styles.virtualContainer}
              style={{
                width: `${columnVirtualizer.getTotalSize()}px`,
                height: `${rowVirtualizer.getTotalSize()}px`,
              }}
            >
              <VirtualizedCells
                virtualRows={virtualRows}
                virtualColumns={virtualColumns}
                scrollableColumns={scrollableColumns}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
